import { useEffect, useState } from 'react';

import {
  Announcement,
  getFormattedLongDate,
  LoaderSkeleton,
  parseLocalDate,
  ProgressBar,
} from '@utilities';
import { getSubscriptionType } from '@helpers';

import { readClientSubscription } from '@api/client';

import styles from './_index.module.scss';

const SubscriptionOverview = () => {
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      setError(null);

      try {
        const subscriptionData = await readClientSubscription();

        setSubscription(subscriptionData[0]);
      } catch (err) {
        setError(`Failed to load subscription info: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  if (isLoading) {
    return (
      <LoaderSkeleton height={105}>
        <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
        <rect height="10" rx="2" ry="2" width="200" x="0" y="75" />
        <rect height="10" rx="2" ry="2" width="225" x="0" y="95" />
      </LoaderSkeleton>
    );
  }

  if (error) {
    return (
      <div>
        <Announcement text="There was an error fetching the subscription info." variant="error" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <Announcement
          header="No subscription information available."
          text="Please contact your Numerator team for more information."
          variant="info"
        />
      </div>
    );
  }

  if (!subscription?.hasStarted) {
    return (
      <div>
        <Announcement
          header={`Great news! Your subscription kicks off ${getFormattedLongDate(parseLocalDate(subscription.startDate))}`}
          text="Please contact your Numerator team for more information."
          variant="info"
        />
      </div>
    );
  }

  const isResetDateValid = (date) => {
    const today = new Date().toISOString().split('T')[0];
    return date >= today;
  };

  return (
    <>
      <div>
        <section className={styles['subscription-overview-usage-row']}>
          <span>Completes for {new Date().toLocaleString('default', { month: 'long' })}</span>
          <span>
            <strong>
              {subscription?.monthlyCompletesUsed} / {subscription?.monthlyCompletesTotal} used
            </strong>
          </span>
        </section>
        <ProgressBar
          className={styles['subscription-overview-progress-bar']}
          hasError={
            subscription?.monthlyCompletesTotal > 0 &&
            subscription?.monthlyCompletesUsed / subscription?.monthlyCompletesTotal >= 1
          }
          max={subscription?.monthlyCompletesTotal}
          value={subscription?.monthlyCompletesUsed}
        />
        <p className={styles['subscription-overview-remaining']}>
          {subscription?.monthlyCompletesTotal - subscription?.monthlyCompletesUsed} remaining
        </p>
      </div>
      <div className={styles['subscription-overview-usage-row-container']}>
        <section className={styles['subscription-overview-usage-row']}>
          <span>Completes for {getSubscriptionType({ type: subscription.type })}</span>
          <span>
            <strong>
              {subscription?.currentPeriod?.usedCompletes} /{' '}
              {subscription?.currentPeriod?.allowedCompletes} used
            </strong>
          </span>
        </section>
        <ProgressBar
          className={styles['subscription-overview-bar-neutral']}
          hasError={
            subscription?.currentPeriod?.allowedCompletes > 0 &&
            subscription?.currentPeriod?.usedCompletes /
              subscription?.currentPeriod?.allowedCompletes >=
              1
          }
          max={subscription?.currentPeriod?.allowedCompletes}
          value={subscription?.currentPeriod?.usedCompletes}
        />
        <p className={styles['subscription-overview-remaining']}>
          {subscription?.currentPeriod?.allowedCompletes -
            subscription?.currentPeriod?.usedCompletes}{' '}
          remaining
        </p>
      </div>
      <ul className={styles['subscription-overview-resets']}>
        <li>
          Next monthly reset:{' '}
          {subscription?.monthlyEndDate && isResetDateValid(subscription.monthlyEndDate)
            ? getFormattedLongDate(parseLocalDate(subscription?.monthlyEndDate))
            : 'N/A'}
        </li>
        <li>
          Next {subscription?.type?.toLowerCase() || 'quarterly'} reset:{' '}
          {subscription?.currentPeriod?.endDate &&
          isResetDateValid(subscription.currentPeriod.endDate) &&
          subscription?.hasFuturePeriods
            ? getFormattedLongDate(parseLocalDate(subscription?.currentPeriod?.endDate))
            : 'N/A'}
        </li>
        {!subscription?.hasFuturePeriods && subscription?.endDate && (
          <li>
            {subscription?.type[0] + subscription?.type.slice(1).toLowerCase() || 'quarterly'} end
            date: {getFormattedLongDate(parseLocalDate(subscription.endDate))}
          </li>
        )}
      </ul>
    </>
  );
};

export default SubscriptionOverview;
